import styled from 'styled-components'
import { MenuIcon } from '@shipt/design-system-icons'
import { trackElementClicked, trackElementViewed } from '@/features/shared/analytics/element'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicHamburgerMenuDrawer } from '@/features/shop/components/Drawer/HamburgerMenuDrawer/DynamicHamburgerMenuDrawer'
import { useEffect, useState } from 'react'
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from '@/features/shared/utils/sessionStorage'
import { useIsPage } from '@/features/shared/hooks/useIsPage'
import { useCanShowViewType } from '@/features/shared/services/ViewedElementTracker/hooks'
import {
  getIsChannelEnabled,
  getIsSmsOptInPending,
} from '@/features/account/services/Preferences/utils'
import { useQueryCommunicationPreferences } from '@/features/account/services/Preferences/queries'
import { SMS_ENTRY_POINT_STORAGE } from '@/features/shared/components/HamburgerMenu/constants'
import { useHydrated } from '@/features/shared/components/ClientOnly'
import { IconButton } from '@shipt/design-system-buttons'

type Props = {
  isGlobalMenu?: boolean
  holidayGHP?: boolean
}

export const HamburgerMenuButton = ({ isGlobalMenu, holidayGHP }: Props) => {
  const { openDialog } = useHandleDialog()
  const { GLOBAL_HOMEPAGE, RETAILER_HOMEPAGE } = useIsPage()
  const [hideDot, setHideDot] = useState(false)
  const hydrated = useHydrated()
  const hiddenThisSession = getSessionStorageItem(SMS_ENTRY_POINT_STORAGE) === 'true'

  const shouldShowDot =
    hydrated && (GLOBAL_HOMEPAGE || RETAILER_HOMEPAGE) && !hideDot && !hiddenThisSession

  const handleOnClick = () => {
    if (shouldShowDot) {
      setSessionStorageItem(SMS_ENTRY_POINT_STORAGE, 'true')
      setHideDot(true)
    }
    openDialog(DynamicHamburgerMenuDrawer, {
      isGlobalMenu,
    })
    trackElementClicked({
      link_name: 'main_menu',
      display_format: 'horizontal_list',
    })
  }

  return (
    <IconWrap>
      <IconButton
        icon={MenuIcon}
        size="md"
        variant={holidayGHP ? 'primary' : 'ghost'}
        onClick={handleOnClick}
        aria-label="main menu"
      />
      {!!shouldShowDot && <DotChecks />}
    </IconWrap>
  )
}

const DotChecks = () => {
  const { data } = useQueryCommunicationPreferences()
  const isOptedIn = getIsChannelEnabled('sms', data?.communications)
  const isSmsOptInPending = getIsSmsOptInPending(data?.communications?.sms)
  const [canShowSmsMarketingEntryBanner] = useCanShowViewType('SmsMarketingEntryPointBanner')
  const shouldShow = canShowSmsMarketingEntryBanner && data && !isSmsOptInPending && !isOptedIn

  useEffect(() => {
    if (shouldShow) {
      trackElementViewed({
        content: 'main_menu_green_dot',
        type: 'notification',
      })
    }
  }, [shouldShow])

  return shouldShow ? <Dot /> : null
}

const Dot = styled.span`
  position: absolute;
  top: 0.5625rem;
  right: 0.5625rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.color.content.feedback.promotion.primary_on_soft};
`

const IconWrap = styled.span`
  position: relative;
  display: inline-flex;
`
